import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Camera, X } from "lucide-react";

interface QRScannerProps {
  onScan: (data: string) => void;
  onClose: () => void;
}

export function QRScanner({ onScan, onClose }: QRScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const startScanner = async () => {
    if (!containerRef.current) return;

    try {
      setError(null);
      const scanner = new Html5Qrcode("qr-scanner-container");
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          scannerRef.current = null;
          scanner.stop().catch(() => {}).finally(() => onScan(decodedText));
        },
        () => {
          // Ignore scan failures (no QR in frame)
        },
      );
      setStarted(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to start camera. Please check permissions.",
      );
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      await scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
      setStarted(false);
    }
    onClose();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-app-text">Scan QR Code</h3>
        <Button type="button" variant="ghost" size="sm" onClick={stopScanner}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div
        id="qr-scanner-container"
        ref={containerRef}
        className="w-full rounded-lg overflow-hidden bg-black"
      />

      {error && (
        <p className="text-sm text-app-danger">{error}</p>
      )}

      {!started && (
        <Button type="button" onClick={startScanner} className="w-full">
          <Camera className="w-4 h-4 mr-2" />
          Start Camera
        </Button>
      )}
    </div>
  );
}
